import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  PageWrap, Header, PeriodoBar, BarraBusqueda, EstadoCarga, AtencionBanner,
  ComparaBanner, KpiCard, MiniDonut, ScoreBar, ModalShell, Tendencia,
  GrafoEnlaces, LeyendaGrafo, SerieTemporal, SemaforoBadge, cs,
  ESTADO, EstadoBadge, SemaforoResumen, ORDEN_ESTADO,
  calcPeriodo, prevPeriodo, lunesISO, todayISO, C, BG, banderaDe, semaforo, fmtFecha,
} from '../analisis/comun';

const fmtPct = (n, total) => (total > 0 ? Math.round((n / total) * 100) + '%' : '–');
const pctConect = (p) => (p.n_alumnos > 0 ? Math.round(((p.n_alumnos - p.n_aislados) / p.n_alumnos) * 100) : null);

// % de convivencia del piso (verde=2, amarillo=1, sobre máximo)
const pctConvivencia = (p) => {
  const lista = (p.piso_scores || []).filter(q => q.total > 0);
  if (!lista.length) return null;
  let s = 0, m = 0;
  lista.forEach(q => { s += q.verde * 2 + (q.amarillo || 0); m += (q.verde + (q.amarillo || 0) + (q.rojo || 0)) * 2; });
  return m === 0 ? null : Math.round((s / m) * 100);
};
// detecta alumnos con señales de problema (algún rojo en sus scores)
const tieneProblema = (al) => (al.scores || []).some(s => (s.rojo || 0) > 0);
const nProblemasPiso = (p) => (p.alumnos || []).filter(tieneProblema).length;

// ── SEMÁFORO DE ESTADO (según reportes hechos + resultados) ──
// % de alumnos sobre los que se ha reportado en el período
const cobertura = (p) => (p.n_alumnos > 0 ? Math.round(((p.n_reportados || 0) / p.n_alumnos) * 100) : null);
const nNoVistos = (p) => (p.alumnos || []).filter(a => a.n_no_visto > 0).length;
const estadoPiso = (p) => {
  if (!p.n_reportes) return 'sin_reportes';                 // ⬜ no hay información
  const conv = pctConvivencia(p);
  if (nProblemasPiso(p) > 0 || (conv != null && conv < 60)) return 'critico';   // 🔴
  const cob = p.n_alumnos > 0 ? (p.n_reportados || 0) / p.n_alumnos : 0;
  if (p.n_aislados > 0 || nNoVistos(p) > 0 || cob < 0.5) return 'atencion';       // 🟡
  return 'bien';                                                                   // 🟢
};

/* ── Tarjeta de alumno ── */
const TarjetaAlumno = ({ alumno }) => {
  const [expanded, setExpanded] = useState(false);
  const totalConex = alumno.n_conexiones;
  const connColor = totalConex >= 3 ? C.verde : totalConex >= 1 ? C.amarillo : C.rojo;
  const problema = tieneProblema(alumno);
  // preguntas con algún rojo, ordenadas por nº de rojos (peores primero)
  const flags = (alumno.scores || []).filter(s => (s.rojo || 0) > 0).sort((a, b) => b.rojo - a.rojo);
  const scoresOrden = [...(alumno.scores || [])].sort((a, b) => (b.rojo || 0) - (a.rojo || 0) || (b.amarillo || 0) - (a.amarillo || 0));
  const borde = problema ? '#fca5a5' : alumno.aislado ? '#fcd34d' : '#86efac';
  const fondo = problema ? '#fef2f2' : alumno.aislado ? '#fffbeb' : '#f0fdf4';
  return (
    <div style={{ background: fondo, border: `1.5px solid ${borde}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}>
        <span style={{ fontSize: 20 }}>{banderaDe(alumno.pais)}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
            {alumno.habitacion && <span style={{ color: '#6b7280', fontSize: 12 }}>🛏 {alumno.habitacion} · </span>}
            {alumno.nombre}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
            <span style={{ color: connColor, fontWeight: 600 }}>🔗 {totalConex} conexión{totalConex !== 1 ? 'es' : ''}</span>
            {alumno.n_superlinks > 0 && <span style={{ color: '#7c3aed' }}>⭐ {alumno.n_superlinks} superlink{alumno.n_superlinks !== 1 ? 's' : ''}</span>}
            {alumno.aislado && <span style={{ color: '#d97706', fontWeight: 700 }}>🔸 Aislado</span>}
            {problema && <span style={{ color: '#dc2626', fontWeight: 700 }}>⚠️ {flags.length} señal{flags.length !== 1 ? 'es' : ''}</span>}
            {alumno.n_no_visto > 0 && <span style={{ color: '#dc2626' }}>❌ No visto ×{alumno.n_no_visto}</span>}
          </div>
          {/* Chips de problemas a la vista sin desplegar */}
          {problema && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
              {flags.slice(0, 3).map(s => (
                <span key={s.id_pregunta} style={{ background: BG.rojo, color: C.rojo, borderRadius: 8, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>
                  {s.emoji} {s.subcategoria} 🔴{s.rojo}
                </span>
              ))}
            </div>
          )}
        </div>
        <span style={{ color: '#9ca3af', fontSize: 16 }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && scoresOrden.length > 0 && (
        <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
          {scoresOrden.map(s => (
            <ScoreBar key={s.id_pregunta} label={s.subcategoria} emoji={s.emoji || ''}
              verde={s.verde || 0} amarillo={s.amarillo || 0} rojo={s.rojo || 0}
              desconozco={s.desconozco || 0} total={s.total || 0} />
          ))}
        </div>
      )}
      {expanded && scoresOrden.length === 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>Sin reportes en el período</div>
      )}
    </div>
  );
};

/* ── Tarjeta de piso ── */
const TarjetaPiso = ({ piso, delta, onClick }) => {
  const pct = pctConect(piso) ?? 0;
  const nProb = nProblemasPiso(piso);
  const estado = estadoPiso(piso);
  const e = ESTADO[estado];
  const cob = cobertura(piso);
  const conv = pctConvivencia(piso);
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '14px 16px',
      border: '1.5px solid #e2e8f0', borderLeftWidth: 5, borderLeftColor: e.color,
      boxShadow: estado === 'critico' ? '0 2px 8px rgba(220,38,38,0.12)' : '0 1px 4px rgba(0,0,0,0.05)',
      marginBottom: 12, cursor: 'pointer',
    }} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <MiniDonut pct={piso.sin_links ? null : pct} size={50} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: 16, color: '#1e3a5f' }}>{piso.abreviatura}</span>
            <EstadoBadge estado={estado} />
            <Tendencia delta={delta} />
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Supervisora: <span style={{ fontWeight: 600 }}>{piso.supervisora}</span>
          </div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>
            📋 {piso.n_reportes || 0} reporte{piso.n_reportes !== 1 ? 's' : ''}
            {piso.n_alumnos > 0 && <> · {piso.n_reportados || 0}/{piso.n_alumnos} alumnos vistos{cob != null && ` (${cob}%)`}</>}
            {piso.ultima_fecha && <> · últ. {fmtFecha(piso.ultima_fecha)}</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <span style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>👥 {piso.n_alumnos}</span>
          <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>🔗 {piso.n_conexiones}</span>
          {piso.n_superlinks > 0 && <span style={{ background: '#f5f3ff', color: '#7c3aed', borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>⭐ {piso.n_superlinks}</span>}
          {conv != null && <span style={{ background: BG[semaforo(conv)], color: C[semaforo(conv)], borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>🏠 {conv}% conviv.</span>}
          {piso.sin_links
            ? <span style={{ background: '#f1f5f9', color: '#64748b', borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>🚫 Sin enlaces definidos</span>
            : piso.n_aislados > 0 && <span style={{ background: '#fef3c7', color: '#d97706', borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>🔸 {piso.n_aislados} aislado{piso.n_aislados !== 1 ? 's' : ''}</span>}
          {nProb > 0 && <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>⚠️ {nProb} con señales</span>}
        </div>
      </div>
    </div>
  );
};

/* ── Modal detalle piso (con pestañas) ── */
const DetallePisoModal = ({ piso, delta, onClose }) => {
  const [tab, setTab] = useState('resumen');
  const pct = pctConect(piso) ?? 0;
  const pctConv = pctConvivencia(piso);
  const pieData = [
    { name: 'Conectados', value: piso.n_alumnos - piso.n_aislados },
    { name: 'Aislados', value: piso.n_aislados },
  ];
  const noVistos = piso.alumnos.filter(a => a.n_no_visto > 0).length;
  const nProb = nProblemasPiso(piso);
  const estado = estadoPiso(piso);
  const cob = cobertura(piso);
  const serie = (piso.serie_conexiones || []).map(s => ({ x: fmtFecha(s.fecha), conexiones: s.conexiones }));
  const tabs = [
    { key: 'resumen', label: '📊 Resumen' },
    { key: 'mapa', label: '🕸 Mapa enlaces' },
  ];
  if ((piso.piso_scores || []).length > 0) tabs.push({ key: 'convivencia', label: '🤝 Convivencia' });
  tabs.push({ key: 'alumnos', label: `👥 Alumnos (${piso.alumnos.length})` });

  return (
    <ModalShell
      titulo={`🏠 ${piso.abreviatura}`}
      subtitulo={`Supervisora: ${piso.supervisora}`}
      colorBorde={ESTADO[estado].color}
      tabs={tabs}
      tab={tab} setTab={setTab} onClose={onClose}
    >
      {tab === 'resumen' && (
        <>
          {/* Banner de estado según reportes */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: ESTADO[estado].bg, border: `1.5px solid ${ESTADO[estado].color}`, borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
            <span style={{ fontSize: 22 }}>{ESTADO[estado].emoji}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: ESTADO[estado].color }}>{ESTADO[estado].label}</div>
              <div style={{ fontSize: 12, color: '#475569', marginTop: 1 }}>
                {estado === 'sin_reportes'
                  ? 'No se ha hecho ningún reporte de este piso en el período: no hay información para valorarlo.'
                  : estado === 'critico'
                    ? 'Se han detectado señales de problema. Revisa los alumnos marcados.'
                    : estado === 'atencion'
                      ? 'Hay aislados, alumnos sin ver o pocos reportes. Conviene revisarlo.'
                      : 'Buen estado: reportes suficientes y sin señales destacables.'}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 12 }}>
            <KpiCard icon="📋" label="Reportes" value={piso.n_reportes || 0} color="#0369a1" bg="#e0f2fe" />
            <KpiCard icon="👁" label="Alumnos vistos" value={piso.n_alumnos > 0 ? `${piso.n_reportados || 0}/${piso.n_alumnos}${cob != null ? ` (${cob}%)` : ''}` : '—'} color="#7c3aed" bg="#ede9fe" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
            <KpiCard icon="👥" label="Alumnos" value={piso.n_alumnos} color="#7c3aed" bg="#ede9fe" />
            <KpiCard icon="🔗" label="Conexiones" value={piso.n_conexiones} color="#0369a1" bg="#e0f2fe" />
            <KpiCard icon={piso.sin_links ? '🚫' : '🔸'} label={piso.sin_links ? 'Sin enlaces' : 'Aislados'} value={piso.sin_links ? '—' : piso.n_aislados} color={piso.sin_links ? '#64748b' : '#d97706'} bg={piso.sin_links ? '#f1f5f9' : '#fef3c7'} />
            <KpiCard icon="⚠️" label="Con señales" value={nProb} color="#dc2626" bg="#fee2e2" />
          </div>
          {piso.sin_links && (
            <div style={{ background: '#f1f5f9', border: '1.5px solid #cbd5e1', borderRadius: 12, padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#475569' }}>
              🚫 <strong>Este piso aún no tiene enlaces de afinidad definidos.</strong> No es lo mismo que tener alumnos aislados: significa que todavía no se ha registrado ninguna relación entre los residentes. Defínelos en la pantalla de Afinidad para poder analizarlos.
            </div>
          )}
          {delta != null && !piso.sin_links && (
            <div style={{ marginBottom: 14, fontSize: 13, color: '#475569' }}>
              Conectividad: <strong>{pct}%</strong> &nbsp; <Tendencia delta={delta} /> <span style={{ color: '#94a3b8' }}>vs período anterior</span>
            </div>
          )}
          {/* Evolución temporal */}
          {serie.length >= 2 && (
            <div style={{ ...cs.sec, marginBottom: 14 }}>
              <SerieTemporal titulo="📈 Evolución de enlaces (últimas semanas)" data={serie}
                series={[{ key: 'conexiones', label: 'Conexiones activas', color: C.indigo }]} />
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
                Si la línea baja, el grupo se está fragmentando (antes había más relaciones que ahora).
              </div>
            </div>
          )}
          {piso.n_alumnos > 0 && !piso.sin_links && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 8 }}>Distribución conectividad</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={32}>
                      <Cell fill={C.verde} /><Cell fill={C.amarillo} />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: C.verde, borderRadius: 2, marginRight: 6 }} />Conectados: <strong>{piso.n_alumnos - piso.n_aislados}</strong> ({fmtPct(piso.n_alumnos - piso.n_aislados, piso.n_alumnos)})</div>
                  <div style={{ fontSize: 13 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: C.amarillo, borderRadius: 2, marginRight: 6 }} />Aislados: <strong>{piso.n_aislados}</strong> ({fmtPct(piso.n_aislados, piso.n_alumnos)})</div>
                  {noVistos > 0 && <div style={{ fontSize: 13, marginTop: 6, color: '#dc2626', fontWeight: 600 }}>❌ {noVistos} sin ver este período</div>}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'mapa' && (
        <>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>
            Mapa de relaciones del piso. Cada círculo es un alumno; las líneas, sus enlaces de afinidad.
          </div>
          <div style={{ ...cs.sec, padding: '10px 8px' }}>
            <GrafoEnlaces alumnos={piso.alumnos.map(a => ({ id_alumno: a.id_alumno, nombre: a.nombre, aislado: a.aislado }))} enlaces={piso.enlaces || []} size={320} />
            <LeyendaGrafo />
          </div>
          {piso.n_aislados > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#d97706', marginBottom: 6 }}>🔸 Alumnos aislados ({piso.n_aislados})</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {piso.alumnos.filter(a => a.aislado).map(a => (
                  <span key={a.id_alumno} style={{ background: BG.amarillo, color: C.amarillo, borderRadius: 8, padding: '3px 9px', fontSize: 12, fontWeight: 700 }}>
                    {banderaDe(a.pais)} {a.nombre}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'convivencia' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <MiniDonut pct={pctConv} size={64} />
            <div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Armonía / convivencia del conjunto</div>
              <SemaforoBadge tipo={semaforo(pctConv)} label={pctConv != null ? `${pctConv}%` : 'Sin datos'} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            Estas valoraciones son sobre <strong>todo el piso en conjunto</strong> (convivencia, ambiente, armonía), no sobre alumnos individuales.
          </div>
          {(piso.piso_scores || []).map(q => (
            <ScoreBar key={q.id_pregunta} label={q.subcategoria} emoji={q.emoji || ''}
              verde={q.verde || 0} amarillo={q.amarillo || 0} rojo={q.rojo || 0} total={q.total || 0} />
          ))}
        </>
      )}

      {tab === 'alumnos' && (
        <>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            Ordenados por prioridad: primero los que tienen <span style={{ color: '#dc2626', fontWeight: 700 }}>⚠️ señales</span>, luego los <span style={{ color: '#d97706', fontWeight: 700 }}>🔸 aislados</span>.
          </div>
          {[...piso.alumnos].sort((a, b) => {
            const pa = tieneProblema(a) ? 2 : a.aislado ? 1 : 0;
            const pb = tieneProblema(b) ? 2 : b.aislado ? 1 : 0;
            return pb - pa || a.n_conexiones - b.n_conexiones;
          }).map(a => (
            <TarjetaAlumno key={a.id_alumno} alumno={a} />
          ))}
        </>
      )}
    </ModalShell>
  );
};

/* ── Modal de detalle de alerta (lista de afectados) ── */
const DetalleAlertaModal = ({ titulo, descripcion, filas, onClose }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px' }}
    onClick={onClose}>
    <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
      onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>{titulo}</div>
          {descripcion && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{descripcion}</div>}
        </div>
        <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 34, height: 34, fontSize: 18, cursor: 'pointer', color: '#475569' }}>✕</button>
      </div>
      <div style={{ overflowY: 'auto', padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filas.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Sin elementos</div>}
        {filas.map((f, i) => (
          <div key={i} style={{ background: '#f8fafc', borderRadius: 10, padding: '8px 12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{f.icon || '•'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{f.nombre}</div>
              {f.extra && <div style={{ fontSize: 11, color: '#64748b' }}>{f.extra}</div>}
            </div>
            {f.tag && <span style={{ background: f.tagBg || '#f1f5f9', color: f.tagColor || '#475569', borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{f.tag}</span>}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
════════════════════════════════════════════════════ */
const AnalisisAlumnos = () => {
  const navigate = useNavigate();
  useAuth();

  const [periodoKey, setPeriodoKey] = useState('mes');
  const [custom, setCustom] = useState({ desde: lunesISO(), hasta: todayISO() });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pisos, setPisos] = useState([]);
  const [pisosPrev, setPisosPrev] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [sortBy, setSortBy] = useState('estado');
  const [pisoDet, setPisoDet] = useState(null);
  const [detalleAlerta, setDetalleAlerta] = useState(null); // { titulo, descripcion, filas }
  const [filtroEstado, setFiltroEstado] = useState(null); // 'critico' | 'atencion' | 'bien' | 'sin_reportes'

  const periodo = periodoKey === 'custom' ? custom : calcPeriodo(periodoKey);
  const { desde, hasta } = periodo;

  const fetchPeriod = useCallback(async (d, h) => {
    const res = await axios.get(`/api/analisis/afinidad?desde=${d}&hasta=${h}`);
    return res.data.pisos || [];
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const pp = prevPeriodo({ desde, hasta });
      const [curr, prev] = await Promise.all([fetchPeriod(desde, hasta), fetchPeriod(pp.desde, pp.hasta)]);
      setPisos(curr); setPisosPrev(prev);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, fetchPeriod]);

  useEffect(() => { cargar(); }, [cargar]);

  const prevMap = useMemo(() => Object.fromEntries(pisosPrev.map(p => [p.id, p])), [pisosPrev]);
  const deltaDe = useCallback((piso) => {
    const prev = prevMap[piso.id];
    const cur = pctConect(piso), prv = prev ? pctConect(prev) : null;
    return cur != null && prv != null ? cur - prv : null;
  }, [prevMap]);

  const pisosFiltrados = useMemo(() => pisos
    .filter(p => !busqueda || p.abreviatura.toLowerCase().includes(busqueda.toLowerCase()))
    .filter(p => !filtroEstado || estadoPiso(p) === filtroEstado)
    .sort((a, b) => {
      if (sortBy === 'estado') { return ORDEN_ESTADO[estadoPiso(a)] - ORDEN_ESTADO[estadoPiso(b)]; }
      if (sortBy === 'problemas') return nProblemasPiso(b) - nProblemasPiso(a);
      if (sortBy === 'aislados') return b.n_aislados - a.n_aislados;
      if (sortBy === 'alumnos') return b.n_alumnos - a.n_alumnos;
      if (sortBy === 'conexiones') return b.n_conexiones - a.n_conexiones;
      return (a.abreviatura || '').localeCompare(b.abreviatura || '');
    }), [pisos, busqueda, sortBy, filtroEstado]);

  const estadoCounts = useMemo(() => {
    const c = { critico: 0, atencion: 0, bien: 0, sin_reportes: 0 };
    pisos.forEach(p => { c[estadoPiso(p)]++; });
    return c;
  }, [pisos]);

  const totalAlumnos = pisos.reduce((s, p) => s + p.n_alumnos, 0);
  // Aislados solo cuenta pisos con enlaces definidos (en sin_links todos serían "aislados" porque no se han registrado relaciones, no porque estén solos)
  const totalAislados = pisos.reduce((s, p) => s + (p.sin_links ? 0 : p.n_aislados), 0);
  const totalConexiones = pisos.reduce((s, p) => s + p.n_conexiones, 0);
  const totalSuperlinks = pisos.reduce((s, p) => s + p.n_superlinks, 0);
  const pisosConAlerta = pisos.filter(p => !p.sin_links && p.n_aislados > 0).length;
  const pisosSinLinks = pisos.filter(p => p.sin_links).length;
  const totalProblemas = pisos.reduce((s, p) => s + nProblemasPiso(p), 0);
  const totalNoVistos = pisos.reduce((s, p) => s + p.alumnos.filter(a => a.n_no_visto > 0).length, 0);
  const pisosSinReportes = pisos.filter(p => !p.n_reportes).length;
  // Base para el pie: solo pisos con enlaces (el resto no tiene datos)
  const totalAlumnosConLinks = pisos.filter(p => !p.sin_links).reduce((s, p) => s + p.n_alumnos, 0);
  const pctConectadosGlobal = totalAlumnosConLinks > 0 ? Math.round(((totalAlumnosConLinks - totalAislados) / totalAlumnosConLinks) * 100) : null;

  // Serie temporal global
  const serieGlobal = useMemo(() => {
    const acc = {};
    pisos.forEach(p => (p.serie_conexiones || []).forEach(s => { acc[s.fecha] = (acc[s.fecha] || 0) + s.conexiones; }));
    return Object.keys(acc).sort().map(f => ({ x: fmtFecha(f), conexiones: acc[f] }));
  }, [pisos]);

  // Convivencia global del conjunto de pisos (agregado de piso_scores de todos los pisos)
  const convGlobal = useMemo(() => {
    const agg = {};
    pisos.forEach(p => (p.piso_scores || []).forEach(q => {
      const k = q.id_pregunta;
      if (!agg[k]) agg[k] = { id_pregunta: k, subcategoria: q.subcategoria, emoji: q.emoji, verde: 0, amarillo: 0, rojo: 0, total: 0 };
      agg[k].verde += q.verde || 0; agg[k].amarillo += q.amarillo || 0; agg[k].rojo += q.rojo || 0; agg[k].total += q.total || 0;
    }));
    return Object.values(agg);
  }, [pisos]);
  const pctConvGlobal = pctConvivencia({ piso_scores: convGlobal });
  const pisosConvMal = useMemo(() => pisos.filter(p => { const c = pctConvivencia(p); return c != null && c < 60; }), [pisos]);
  const listaConvMal = useMemo(() => pisosConvMal.map(p => ({
    icon: '🏠',
    nombre: p.abreviatura,
    extra: `Supervisora: ${p.supervisora}`,
    tag: `🏠 ${pctConvivencia(p)}%`, tagBg: BG.rojo, tagColor: C.rojo,
  })), [pisosConvMal]);

  const pieGlobal = [
    { name: 'Conectados', value: totalAlumnosConLinks - totalAislados },
    { name: 'Aislados', value: totalAislados },
  ];

  // Listas precalculadas para el detalle de alertas
  const listaProblemas = useMemo(() => pisos.flatMap(p =>
    p.alumnos.filter(tieneProblema).map(a => ({
      icon: banderaDe(a.pais),
      nombre: a.nombre,
      extra: `${p.abreviatura} · Hab. ${a.habitacion || '—'}`,
      tag: `🔴 ${(a.scores || []).reduce((s, sc) => s + (sc.rojo || 0), 0)} señal${(a.scores || []).reduce((s, sc) => s + (sc.rojo || 0), 0) !== 1 ? 'es' : ''}`,
      tagBg: '#fee2e2', tagColor: '#dc2626',
    }))
  ), [pisos]);

  const listaAislados = useMemo(() => pisos.filter(p => !p.sin_links).flatMap(p =>
    p.alumnos.filter(a => a.aislado).map(a => ({
      icon: banderaDe(a.pais),
      nombre: a.nombre,
      extra: `${p.abreviatura} · Hab. ${a.habitacion || '—'}`,
      tag: `🔗 0 conexiones`, tagBg: '#fef3c7', tagColor: '#d97706',
    }))
  ), [pisos]);

  const listaSinLinks = useMemo(() => pisos.filter(p => p.sin_links).map(p => ({
    icon: '🏠',
    nombre: p.abreviatura,
    extra: `Supervisora: ${p.supervisora} · ${p.n_alumnos} alumno${p.n_alumnos !== 1 ? 's' : ''}`,
    tag: 'Sin datos', tagBg: '#f1f5f9', tagColor: '#64748b',
  })), [pisos]);

  const listaSinReportes = useMemo(() => pisos.filter(p => !p.n_reportes).map(p => ({
    icon: '🏠',
    nombre: p.abreviatura,
    extra: `Supervisora: ${p.supervisora} · ${p.n_alumnos} alumno${p.n_alumnos !== 1 ? 's' : ''}`,
    tag: '⬜ 0 reportes', tagBg: '#f1f5f9', tagColor: '#64748b',
  })), [pisos]);

  const listaNoVistos = useMemo(() => pisos.flatMap(p =>
    p.alumnos.filter(a => a.n_no_visto > 0).map(a => ({
      icon: banderaDe(a.pais),
      nombre: a.nombre,
      extra: `${p.abreviatura} · Hab. ${a.habitacion || '—'}`,
      tag: `❌ No visto ×${a.n_no_visto}`, tagBg: '#fee2e2', tagColor: '#dc2626',
    }))
  ), [pisos]);

  const atencion = useMemo(() => {
    const items = [];
    if (totalProblemas > 0) items.push({
      icon: '⚠️', severidad: 'rojo', n: totalProblemas,
      texto: `${totalProblemas} alumno${totalProblemas !== 1 ? 's' : ''} con señales de problema`,
      onClick: () => setDetalleAlerta({ titulo: '⚠️ Alumnos con señales de problema', descripcion: 'Alumnos con al menos una valoración 🔴 en el período.', filas: listaProblemas }),
    });
    if (totalAislados > 0) items.push({
      icon: '🔸', severidad: 'amarillo', n: totalAislados,
      texto: `${totalAislados} alumno${totalAislados !== 1 ? 's' : ''} aislado${totalAislados !== 1 ? 's' : ''} en ${pisosConAlerta} piso${pisosConAlerta !== 1 ? 's' : ''}`,
      onClick: () => setDetalleAlerta({ titulo: '🔸 Alumnos aislados', descripcion: 'Sin ningún enlace de afinidad con otros residentes del piso.', filas: listaAislados }),
    });
    if (pisosConvMal.length > 0) items.push({
      icon: '🏠', severidad: 'rojo', n: pisosConvMal.length,
      texto: `${pisosConvMal.length} piso${pisosConvMal.length !== 1 ? 's' : ''} con mala convivencia general`,
      onClick: () => setDetalleAlerta({ titulo: '🏠 Pisos con mala convivencia', descripcion: 'Convivencia general del piso por debajo del 60% (valoraciones sobre el ambiente del conjunto, no sobre alumnos concretos).', filas: listaConvMal }),
    });
    if (pisosSinLinks > 0) items.push({
      icon: '🚫', severidad: 'gris', n: pisosSinLinks,
      texto: `${pisosSinLinks} piso${pisosSinLinks !== 1 ? 's' : ''} sin datos de afinidad registrados`,
      onClick: () => setDetalleAlerta({ titulo: '🚫 Pisos sin afinidad registrada', descripcion: 'Estos pisos tienen 2+ alumnos pero aún no se han registrado sus relaciones de afinidad. No significa aislamiento, solo que los datos no se han introducido.', filas: listaSinLinks }),
    });
    if (totalNoVistos > 0) items.push({
      icon: '❌', severidad: 'amarillo', n: totalNoVistos,
      texto: `${totalNoVistos} alumno${totalNoVistos !== 1 ? 's' : ''} no vistos este período`,
      onClick: () => setDetalleAlerta({ titulo: '❌ Alumnos no vistos', descripcion: 'En el reporte diario, la supervisora marcó "no lo he visto" al menos una vez en este período. Puede indicar falta de contacto con el residente.', filas: listaNoVistos }),
    });
    if (pisosSinReportes > 0) items.push({
      icon: '⬜', severidad: 'gris', n: pisosSinReportes,
      texto: `${pisosSinReportes} piso${pisosSinReportes !== 1 ? 's' : ''} sin ningún reporte en el período`,
      onClick: () => setDetalleAlerta({ titulo: '⬜ Pisos sin reportes', descripcion: 'No se ha registrado ningún reporte diario de estos pisos en el período. No hay información para valorarlos: conviene asegurarse de que se están haciendo los reportes.', filas: listaSinReportes }),
    });
    return items;
  }, [totalProblemas, totalAislados, pisosConAlerta, pisosSinLinks, totalNoVistos, pisosSinReportes, listaProblemas, listaAislados, listaSinLinks, listaNoVistos, listaSinReportes, pisosConvMal, listaConvMal]);

  const pp = prevPeriodo({ desde, hasta });
  const vacio = !loading && !error && pisosFiltrados.length === 0;

  return (
    <PageWrap>
      <Header icon="🤝" title="Análisis de Alumnos" count={pisos.length} countLabel="pisos" onBack={() => navigate(-1)} />
      <PeriodoBar periodoKey={periodoKey} setPeriodoKey={setPeriodoKey} custom={custom} setCustom={setCustom} />
      {!loading && !error && <ComparaBanner prev={pp} />}

      <EstadoCarga loading={loading} error={error} onRetry={cargar} />

      {!loading && !error && (
        <>
          <AtencionBanner items={atencion} okTexto="Sin alumnos aislados ni pendientes de ver." />

          {/* Semáforo global — estado de los pisos según reportes (clic para filtrar) */}
          <SemaforoResumen counts={estadoCounts} filtro={filtroEstado} setFiltro={setFiltroEstado} />

          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, padding: '4px 14px 0' }}>
            <KpiCard icon="👥" label="Total alumnos" value={totalAlumnos} color="#7c3aed" bg="#ede9fe" />
            <KpiCard icon="⚠️" label="Con señales" value={totalProblemas} color="#dc2626" bg="#fee2e2" />
            <KpiCard icon="🔸" label="Aislados" value={totalAislados} sub={pctConectadosGlobal != null ? `${pctConectadosGlobal}% conectados` : null} color="#d97706" bg="#fef3c7" />
            <KpiCard icon="🔗" label="Conexiones" value={totalConexiones} color="#0369a1" bg="#e0f2fe" />
          </div>

          {/* Evolución temporal global */}
          {serieGlobal.length >= 2 && (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, margin: '12px 14px', border: '1px solid #e5e7eb' }}>
              <SerieTemporal titulo="📈 Evolución de enlaces (todos los pisos)" data={serieGlobal}
                series={[{ key: 'conexiones', label: 'Conexiones activas', color: C.indigo }]} height={170} />
            </div>
          )}

          {/* Gráfico global — solo pisos con enlaces */}
          {totalAlumnosConLinks > 0 && (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, margin: '12px 14px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 4 }}>Conectividad global</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Solo pisos con afinidad registrada ({pisos.length - pisosSinLinks} de {pisos.length})</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={pieGlobal} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={30}>
                      <Cell fill={C.verde} /><Cell fill={C.amarillo} />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: C.verde, borderRadius: 2, marginRight: 6 }} />Conectados: <strong>{totalAlumnosConLinks - totalAislados}</strong> ({fmtPct(totalAlumnosConLinks - totalAislados, totalAlumnosConLinks)})</div>
                  <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: C.amarillo, borderRadius: 2, marginRight: 6 }} />Aislados: <strong>{totalAislados}</strong> ({fmtPct(totalAislados, totalAlumnosConLinks)})</div>
                  <div style={{ fontSize: 13 }}><span style={{ display: 'inline-block', width: 10, height: 10, background: '#a855f7', borderRadius: 2, marginRight: 6 }} />Superlinks: <strong>{totalSuperlinks}</strong></div>
                </div>
              </div>
            </div>
          )}

          {/* Convivencia global de los pisos (valoración del conjunto, no de alumnos) */}
          {convGlobal.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, margin: '12px 14px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 2 }}>🏠 Convivencia de los pisos</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>Valoración del ambiente y la armonía del conjunto de cada piso (no de alumnos concretos).</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
                <MiniDonut pct={pctConvGlobal} size={64} />
                <div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Convivencia media de todos los pisos</div>
                  <SemaforoBadge tipo={semaforo(pctConvGlobal)} label={pctConvGlobal != null ? `${pctConvGlobal}%` : 'Sin datos'} />
                  {pisosConvMal.length > 0 && <div style={{ fontSize: 12, color: C.rojo, fontWeight: 600, marginTop: 4 }}>🏠 {pisosConvMal.length} piso{pisosConvMal.length !== 1 ? 's' : ''} por debajo del 60%</div>}
                </div>
              </div>
              {convGlobal.map(q => (
                <ScoreBar key={q.id_pregunta} label={q.subcategoria} emoji={q.emoji || ''}
                  verde={q.verde || 0} amarillo={q.amarillo || 0} rojo={q.rojo || 0} total={q.total || 0} />
              ))}
            </div>
          )}

          <BarraBusqueda
            busqueda={busqueda} setBusqueda={setBusqueda}
            orden={sortBy} setOrden={setSortBy}
            placeholder="🔍 Buscar piso..."
            ordenes={[
              { key: 'estado', label: '🚦 Estado' },
              { key: 'problemas', label: '⚠️ Más señales' },
              { key: 'aislados', label: '🔸 Más aislados' },
              { key: 'alumnos', label: '👥 Más alumnos' },
              { key: 'conexiones', label: '🔗 Más conexiones' },
              { key: 'nombre', label: 'A–Z nombre' },
            ]}
          />

          <div style={{ padding: '0 14px' }}>
            <EstadoCarga vacio={vacio} />
            {pisosFiltrados.map(p => (
              <TarjetaPiso key={p.id} piso={p} delta={deltaDe(p)} onClick={() => setPisoDet(p)} />
            ))}
          </div>
        </>
      )}

      {pisoDet && <DetallePisoModal piso={pisoDet} delta={deltaDe(pisoDet)} onClose={() => setPisoDet(null)} />}
      {detalleAlerta && <DetalleAlertaModal {...detalleAlerta} onClose={() => setDetalleAlerta(null)} />}
    </PageWrap>
  );
};

export default AnalisisAlumnos;
